import { cn } from '@/lib/utils';
import useDip from './useDip';
import VignetteFrame, { CHROME_BAR_HEIGHT, ChromeBar, Crumbs } from './VignetteFrame';

/** The app's top bar, in layout px (VignetteFrame's ChromeBar). */
export const BAR_HEIGHT = CHROME_BAR_HEIGHT;

/*
 * A trail's fades when the window changes page (useDip): the old one fades
 * out over TRAIL_OUT_MS, useDip's DIP_OUT_MS, the curve its swap is timed
 * on, and is dropped at the swap; the new one comes in from there over
 * TRAIL_IN_MS.
 */
const TRAIL_IN_MS = 250;
const TRAIL_OUT_MS = 150;

/**
 * A story's app window: the app's top bar and, below it, a viewport of a
 * fixed height the page scrolls inside (the story's camera moves the page,
 * never the window). Its outer height never changes, whatever happens
 * inside, so nothing around it moves. Laid out at `baseWidth` and fitted to
 * the frame it gets (VignetteFrame), `height` layout px tall in all.
 *
 * - `trails`: the breadcrumb of each page the story visits, by page name;
 *   `page` is the page showing. When it changes the trail dips with it
 *   (the old one fades out and is hidden at the swap, so the two never show
 *   together, then the new one fades in). A trail that is not a list of
 *   crumbs is drawn as given (a public page's address, say).
 * - `children(viewportHeight)`: the viewport's content (the story's scene),
 *   given the viewport's height in layout px.
 */
export default function StoryWindow({ baseWidth, height, trails, page, reduced = false, className, children }) {
    const visible = useDip(page, { reduced });
    const viewportHeight = height - BAR_HEIGHT;

    return (
        <VignetteFrame bare baseWidth={baseWidth} className={className}>
            <div data-story-window="" className="flex flex-col" style={{ height }}>
                <ChromeBar>
                    <span className="grid min-w-0">
                        {Object.entries(trails).map(([name, trail]) => (
                            <span
                                key={name}
                                data-trail={name}
                                className={cn(
                                    'col-start-1 row-start-1 flex min-w-0 transition-opacity ease-mk',
                                    visible(name) ? 'opacity-100' : 'opacity-0',
                                    visible.gone(name) && 'invisible',
                                )}
                                style={{ transitionDuration: `${visible(name) ? TRAIL_IN_MS : TRAIL_OUT_MS}ms` }}
                            >
                                {Array.isArray(trail) ? <Crumbs breadcrumb={trail} /> : trail}
                            </span>
                        ))}
                    </span>
                </ChromeBar>
                <div data-story-viewport="" className="relative min-h-0 flex-1 overflow-hidden bg-white">
                    {children(viewportHeight)}
                </div>
            </div>
        </VignetteFrame>
    );
}
